import { ArrowRightLeft } from "lucide-react";
import { useDispatch } from "react-redux";

import {
  IgAnalyticsTooltip,
  IgIconButton,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import { VariationFe } from "@intelligems/ig-types";
import { MenuItem, SelectChangeEvent, Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useSelector } from "@src/store";
import {
  getExperienceAnalyticsFilters,
  refreshData,
  updateDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { selectedVariations } from "@src/store/slices/variations";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { useQueryClient } from "@tanstack/react-query";

interface IgTypographyOrSelectTbProps {
  value: string | undefined;
  variations: VariationFe[];
  onGroupChange: (
    key: keyof IExperienceDashboardFilters,
    value: string
  ) => void;
  groupKey: "compareGroupA" | "compareGroupB";
}

const IgTypographyOrSelectTb = ({
  value,
  variations,
  onGroupChange,
  groupKey,
}: IgTypographyOrSelectTbProps) => {
  const selectValue = value ?? "";

  const otherGroupKey =
    groupKey === "compareGroupA" ? "compareGroupB" : "compareGroupA";
  const otherGroupValue = useSelector(
    (state) => state.dashboardFilters[otherGroupKey]
  );

  if (selectValue === "") {
    return (
      <Skeleton
        variant="rectangular"
        height={30}
        width={124.5}
        sx={{ borderRadius: "4px" }}
      />
    );
  }

  const selectedVariation = variations.find((v) => v.id === selectValue);
  const displayName = selectedVariation?.name ?? selectValue;

  if (variations.length === 2) {
    return <IgTypography sx={{ fontWeight: 600 }}>{displayName}</IgTypography>;
  }

  return (
    <IgSelect
      variant="outlined"
      value={selectValue}
      onChange={(e: SelectChangeEvent<any>) => {
        onGroupChange(groupKey, e.target.value);
      }}
      sx={{
        backgroundColor: "transparent",
        "& .MuiSelect-select": {
          padding: "5px 32px 5px 14px",
          fontWeight: 600,
        },
      }}
    >
      {/* Ensure selected value is still visible */}
      <MenuItem key={selectValue} value={selectValue}>
        {displayName}
      </MenuItem>

      {/* Only show other options that are NOT selected AND NOT selected in the other group */}
      {variations
        .filter(
          (v) =>
            v.id !== selectValue && // not currently selected
            v.id !== otherGroupValue // not selected in other group
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((group) => (
          <MenuItem key={group.id} value={group.id}>
            {group.name}
          </MenuItem>
        ))}
    </IgSelect>
  );
};

export const CompareGroupsFilter = () => {
  const variations = useSelector(selectedVariations);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const dispatch = useDispatch();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const refreshDashboardData = () => {
    dispatch(refreshData());

    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.some(
          (key) =>
            typeof key === "object" &&
            JSON.stringify(key).includes("dashboards")
        ),
    });
  };

  const onGroupChange = (
    key: keyof IExperienceDashboardFilters,
    value: string
  ) => {
    dispatch(updateDashboardFilter({ key, value }));
    if (key == "compareGroupA") {
      refreshDashboardData();
    }
  };

  const onGroupSwap = () => {
    const groupA = dashboardFilters.compareGroupA;
    const groupB = dashboardFilters.compareGroupB;

    dispatch(updateDashboardFilter({ key: "compareGroupA", value: groupB }));
    dispatch(updateDashboardFilter({ key: "compareGroupB", value: groupA }));

    refreshDashboardData();
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      spacing={1.5}
      wrap="nowrap"
    >
      <Grid>
        <IgTypography sx={{ color: theme.palette.text.secondary }}>
          Compare
        </IgTypography>
      </Grid>
      <Grid>
        <IgTypographyOrSelectTb
          value={dashboardFilters.compareGroupA}
          variations={variations}
          onGroupChange={onGroupChange}
          groupKey="compareGroupA"
        />
      </Grid>
      <Grid>
        <IgAnalyticsTooltip title="Swap groups">
          <IgIconButton onClick={onGroupSwap}>
            <ArrowRightLeft size={16} />
          </IgIconButton>
        </IgAnalyticsTooltip>
      </Grid>
      <Grid>
        <IgTypographyOrSelectTb
          value={dashboardFilters.compareGroupB}
          variations={variations}
          onGroupChange={onGroupChange}
          groupKey="compareGroupB"
        />
      </Grid>
    </Grid>
  );
};
